import { clsx } from "clsx";
import React, { useEffect, useRef } from "react";
import AppFooter from "web/react/components/app-footer/app-footer";
import { BrandAdContainer } from "web/react/components/brand-ad-container";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { Divider } from "web/react/components/divider";
import { FooterProps } from "web/react/components/footer/footer";
import { MoreLikeThisGrid } from "web/react/components/more-like-this-grid/more-like-this-grid";
import { ProductOverlay } from "web/react/components/product-overlay";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedRail } from "web/react/components/related-rail/related-rail";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { InfoCallout } from "web/react/emo/info-callout";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
} from "web/types/serializers";
import { LastSeenTimeCallout } from "./last-seen-time-callout";
import * as styles from "./out-of-stock-product-page.css";
import { PreviouslyAvailableFromWithModal } from "./previously-available-from";
import { ProductArea } from "./product-area";
import { ProductDetails } from "./product-details";
import { sendAnalyticsEvent } from "./utils";

const getInfoCalloutTitle = (
    buyOptions: ProductBuyOptionsSerializer[],
    uniqueRetailerCount: number,
    isCheckout: boolean
): string => {
    if (!buyOptions?.length) return gettext("oos_product.info_callout_no_retailers");
    else if (isCheckout) return gettext("oos_product.info_callout_icon");
    else {
        return uniqueRetailerCount === 2
            ? gettext("oos_product.info_callout_affiliate_two_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
              })
            : uniqueRetailerCount > 2
            ? gettext("oos_product.info_callout_affiliate_multiple_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
                  retailer_count: uniqueRetailerCount - 1,
              })
            : gettext("oos_product.info_callout_affiliate", {
                  retailer_name: buyOptions[0].retailer_name,
              });
    }
};

function SectionDivider({ isMobileOnly }: { isMobileOnly?: boolean }): React.ReactElement {
    return (
        <div className={clsx({ [styles.hideDesktop]: isMobileOnly })}>
            <Divider />
        </div>
    );
}

export interface OutOfStockProductPageProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    mainBuyOption: ProductBuyOptionsSerializer;
    isCheckout: boolean;
    isOverlay?: boolean;
    footerData?: FooterProps;
}
export function OutOfStockProductPage({
    productArea,
    productLayout,
    isOverlay,
    isCheckout,
    mainBuyOption,
    footerData,
}: OutOfStockProductPageProps): React.ReactElement {
    const uniqueRetailers = productArea.unique_retailers;
    const uniqueRetailerCount = uniqueRetailers.length;
    const productDetailsContainerRef = useRef<HTMLDivElement>(null);
    const topOfPDPRef = useRef<HTMLDivElement>(null);

    const overlayRef = useRef(null);

    const {
        brand_ad: brandAd,
        related_designers_rail: relatedDesignersRail,
        related_products: relatedProducts,
        product_breadcrumbs: productBreadcrumbs,
        related_search_rail: relatedSearchRail,
        recently_viewed_products: recentlyViewedProducts,
        product_analytics_data: productAnalyticsData,
    } = productLayout;

    useEffect(() => {
        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    return (
        <>
            {!isOverlay && (
                <div ref={overlayRef}>
                    <ProductOverlay parentRef={overlayRef} />
                </div>
            )}
            <div className={styles.pageWrapper} ref={topOfPDPRef}>
                {!isOverlay && <CustomerCaptureOverlay />}
                <div
                    className={clsx(
                        styles.pageContent,
                        styles.pageContentPadding[isOverlay ? "overlay" : "standard"]
                    )}
                >
                    <div className={styles.heroSection}>
                        <InfoCallout
                            variant="information"
                            title={getInfoCalloutTitle(
                                productArea?.buy_options,
                                uniqueRetailerCount,
                                isCheckout
                            )}
                        />
                        {mainBuyOption.last_seen_in_stock && (
                            <LastSeenTimeCallout
                                productId={productArea.product.product_id}
                                mainBuyOption={mainBuyOption}
                                isCheckout={isCheckout}
                            />
                        )}
                        <div className={styles.mainBuyOptionPlusOtherOptionsContainer}>
                            <div className={styles.productAreaContainer}>
                                <ProductArea
                                    productArea={productArea}
                                    productLayout={productLayout}
                                    uniqueRetailerCount={uniqueRetailerCount}
                                    isCheckout={isCheckout}
                                    mainBuyOption={mainBuyOption}
                                    productDetailsContainerRef={productDetailsContainerRef}
                                />
                            </div>
                            <div className={styles.previouslyAvailableFromContainer}>
                                <PreviouslyAvailableFromWithModal
                                    uniqueRetailers={uniqueRetailers}
                                    productId={productArea.product.product_id}
                                />
                            </div>
                        </div>
                    </div>
                    <SectionDivider />
                    {brandAd && (
                        <div data-testid="brand-ad">
                            <BrandAdContainer {...brandAd} />
                        </div>
                    )}
                    {relatedProducts && (
                        <MoreLikeThisGrid
                            title={gettext("oos_product.alternatives_you_might_like")}
                            section={relatedProducts.sections[0]}
                            isInStock
                        />
                    )}
                    {relatedDesignersRail?.candidates?.length && (
                        <RelatedRail
                            title={relatedDesignersRail.title}
                            candidates={relatedDesignersRail.candidates}
                            analyticsLabel="designers"
                            logoSize="xxl"
                        />
                    )}
                    <SectionDivider isMobileOnly />
                    <div
                        ref={productDetailsContainerRef}
                        data-testid="full-product-details-container"
                    >
                        <ProductDetails
                            topOfPDPRef={topOfPDPRef}
                            isOverlay={isOverlay}
                            productArea={productArea}
                            productBreadcrumbs={productBreadcrumbs}
                        />
                    </div>
                    {!!relatedSearchRail?.related_searches.length && (
                        <RelatedSearchesRail
                            title={gettext("oos_product.people_also_searched_for")}
                            relatedSearches={relatedSearchRail?.related_searches}
                            onClick={() =>
                                sendAnalyticsEvent("related_searches", "click", "pdp_oos")
                            }
                        />
                    )}
                    <SectionDivider isMobileOnly />
                    {!!recentlyViewedProducts && (
                        <RecentlyViewed
                            title={recentlyViewedProducts.title}
                            products={recentlyViewedProducts.products}
                            is_loading={recentlyViewedProducts.is_loading}
                            current_product_id={recentlyViewedProducts.current_product_id}
                            in_overlay={recentlyViewedProducts.in_overlay}
                        />
                    )}
                </div>
            </div>
            {footerData && (
                <AppFooter
                    data={footerData.data}
                    helpCentreURLs={footerData.helpCentreURLs}
                    country={footerData.country}
                />
            )}
        </>
    );
}
